/** No persistent response cache: each request rechecks server authorization. */
export const requestParams = (state) => ({
  dataset_id: state.datasetId, version: state.version,
  coordinate_space_id: state.coordinateSpace,
  ...Object.fromEntries(Object.entries(state.filters || {}).filter(([key, value]) => key !== 'region_query' && value !== '' && value !== null && value !== undefined)),
});
const stable = (value) => JSON.stringify(value, function (key, item) {
  return item && !Array.isArray(item) && typeof item === 'object' ? Object.fromEntries(Object.keys(item).sort().map((name) => [name, item[name]])) : item;
});
export const requestKeys = (state) => {
  const identity = [state.datasetId, state.version, state.cohort];
  const scientific = requestParams(state);
  return {
    spatial: stable([identity, scientific, state.colorGeneId]),
    table: stable([identity, scientific, state.resultQuery]),
    evidence: stable([identity, state.selectedEntityId]),
    adjacent: stable([identity, scientific, state.selectedEntityId, state.resultQuery?.page_size, state.resultQuery?.sort_by, state.resultQuery?.sort_direction]),
  };
};
/** Cancellation guards completions even when a transport ignores AbortSignal. */
export function createRequestCoordinator(onDenied = () => {}) {
  const channels = new Map();
  const cancel = (channel) => { channels.get(channel)?.abort(); channels.delete(channel); };
  const clear = () => { for (const channel of channels.keys()) cancel(channel); };
  return {
    cancel, clear,
    run(channel, operation, success, failure) {
      cancel(channel);
      const controller = new AbortController();
      channels.set(channel, controller);
      Promise.resolve().then(() => operation(controller.signal)).then((data) => {
        if (channels.get(channel) === controller && !controller.signal.aborted) success(data);
      }).catch((error) => {
        if (channels.get(channel) !== controller || controller.signal.aborted) return;
        // A missing cell isn't a cohort revocation. Explicit authorization failures
        // still purge protected responses; partial evidence uses named slot states.
        const missingCell = ['evidence', 'activity'].includes(channel) && error.status === 404;
        if (channel !== 'meshes' && !missingCell && [401, 403, 404].includes(error.status)) { clear(); onDenied(error); }
        if (error.name !== 'AbortError') failure(error);
      });
      return () => { if (channels.get(channel) === controller) cancel(channel); };
    },
  };
}

/** Keys consume normalized durable collection state, never an expiring handle. */
export function collectionRequestKeys(state, transient = {}) {
  const identity = [state.datasetId, state.version, state.collection];
  const membership = [identity, state.filters];
  return {
    membership: stable(membership),
    table: stable([membership, state.tableOrder, transient.table || {}]),
    adjacent: stable([membership, state.tableOrder, state.selectedEntityId, transient.table?.page_size || 25]),
    evidence: stable([identity, state.selectedEntityId, transient.genePage || {offset: 0, limit: 32}]),
    encoding: stable([identity, state.colorMode, state.colorGeneId]),
    tiles: stable([membership, state.frame, state.camera, transient.viewport || {}, state.selectedEntityId]),
    summary: stable([membership, transient.summary || {groupings: ['class', 'region']}]),
    export: stable([membership, transient.export || {}]),
  };
}

/** Abortable bounded backoff: no interval and no live timeout after teardown. */
export async function pollCollection(read, signal, {maxAttempts = 30, delay = 250, maxDelay = 5000, sleep} = {}) {
  const wait = sleep || ((ms, abort) => new Promise((resolve, reject) => {
    const stop = () => { clearTimeout(timer); abort.removeEventListener('abort', stop); reject(Object.assign(new Error('Aborted'), {name: 'AbortError'})); };
    const timer = setTimeout(() => { abort.removeEventListener('abort', stop); resolve(); }, ms);
    abort.addEventListener('abort', stop, {once: true});
    if (abort.aborted) stop();
  }));
  for (let attempt = 0; attempt < Math.min(180, maxAttempts); attempt++) {
    signal.throwIfAborted();
    let data;
    try { data = await read(signal); }
    catch (error) { if (error.code !== 'order_pending') throw error; data = {state: 'processing'}; }
    signal.throwIfAborted();
    if (['error','failed','cancelled','expired'].includes(data.state)) throw Object.assign(new Error('Collection work unavailable'), {code: data.error || data.state});
    if (!['processing','queued','running','pending'].includes(data.state)) return data;
    if (attempt + 1 < Math.min(180, maxAttempts)) await wait(Math.min(maxDelay, delay * 2 ** attempt), signal);
  }
  throw Object.assign(new Error('Still waiting for background processing. Work is retained; check again to resume waiting.'), {code: 'poll_limit'});
}

/** Memory only. Values are never returned as authority: consumers must fetch again. */
export function createCollectionCache(limit = 128 * 1024 * 1024) {
  const maximum = typeof limit === 'number' && Number.isFinite(limit)
    ? Math.max(0, Math.min(128 * 1024 * 1024, Math.floor(limit))) : 128 * 1024 * 1024;
  const entries = new Map();
  let bytes = 0;
  const remove = key => { const item = entries.get(key); if (item) bytes -= item.bytes; entries.delete(key); };
  return {
    get bytes() { return bytes; },
    get size() { return entries.size; },
    clear() { entries.clear(); bytes = 0; },
    put(identity, key, value) {
      const cacheKey = stable([identity, key]);
      remove(cacheKey);
      // UTF-16 accounting is conservative for JS strings; include key/entry overhead.
      const serialized = JSON.stringify(value);
      const size = 2 * (serialized.length + cacheKey.length) + 128;
      if (size > maximum) return;
      while (bytes + size > maximum) remove(entries.keys().next().value);
      entries.set(cacheKey, {bytes: size, serialized}); bytes += size;
    },
  };
}
